import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import EscarapelaIcon from './EscarapelaIcon';
import { BADGES_INFO } from '../../mockData';
import { avatarPublicUrl } from '../../lib/memberHelpers';
import { motion } from 'framer-motion';

export const ClubTypeIcon = ({ tipo, className = "w-3 h-3" }) => {
  const normalized = String(tipo || '').toLowerCase();
  if (normalized === 'mateada') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M17 8c0 4.5-2 9-5 9s-5-4.5-5-9h10z"></path>
        <path d="M14 3l-2 5"></path>
      </svg>
    );
  }
  if (normalized === 'asado') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"></path>
      </svg>
    );
  }
  return <SafeIcon name="Star" className={className} />;
};

export const MemberCard = ({ member, capituloName }) => {
  const { isAuthenticated } = useApp();
  const featuredBadges = member.badges?.filter(b => b.featured).slice(0, 3) || [];
  const isAbanderado = member.badges?.some(b => b.id === 'abanderado');
  const showSpecialty = member.ofrezco != null || member.busco != null;
  const profileTo = isAuthenticated && member.id
    ? `/miembro/${member.id}`
    : '/login';

  return (
    <Link to={profileTo} className="bg-white rounded-2xl p-6 shadow-sm border border-stone-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full relative overflow-hidden group">
      <div className="absolute top-0 left-0 w-1 h-full bg-[#F6B40E] opacity-0 group-hover:opacity-100 transition-opacity" />
      {isAbanderado && (
        <div className="absolute top-0 right-0 w-20 h-20 z-0 pointer-events-none">
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-sm opacity-90">
            <path d="M 0 0 L 100 0 L 100 100 Z" fill="#74ACDF" />
            <path d="M 33.33 0 L 100 0 L 100 66.66 Z" fill="#FFFFFF" />
            <path d="M 66.66 0 L 100 0 L 100 33.33 Z" fill="#74ACDF" />
            <circle cx="75" cy="25" r="4.5" fill="#F6B40E" />
          </svg>
        </div>
      )}
      <div className="flex items-start gap-4 mb-6 relative z-10">
        <div className="relative">
          <img
            src={
              avatarPublicUrl(member.foto_url) ||
              'https://api.dicebear.com/7.x/initials/svg?seed=' + encodeURIComponent(member.nombre || '?')
            }
            alt={member.nombre}
            className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm ring-4 ring-[#74ACDF]/10 bg-white"
          />
          {member.role === 'admin' && (
            <div className="absolute -bottom-1 -right-1 bg-[#F6B40E] text-white p-1 rounded-full shadow-sm" title="Administrador">
              <SafeIcon name="Shield" className="w-3 h-3" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <h3 className="font-editorial font-bold text-xl text-[#1B2A4E] leading-tight group-hover:text-[#74ACDF] transition-colors flex flex-wrap items-center gap-2">
            {member.nombre}
            {featuredBadges.length > 0 && (
              <span className="flex items-center gap-1.5 ml-1">
                {featuredBadges.map(b => {
                  const info = BADGES_INFO[b.id];
                  if (!info) return null;
                  const isAbanderadoBadge = b.id === 'abanderado';
                  return (
                    <div key={b.id} title={info.name} className={`flex items-center justify-center rounded-full p-1 shadow-sm border ${isAbanderadoBadge ? 'bg-white border-stone-200' : 'bg-stone-50 border-stone-200 text-stone-600'}`}>
                      {isAbanderadoBadge ? <EscarapelaIcon className="w-3.5 h-3.5" /> : <SafeIcon name={info.icon} className="w-3.5 h-3.5" />}
                    </div>
                  );
                })}
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="label-technical text-[#74ACDF]">{member.rubro}</span>
            <span className="w-1 h-1 rounded-full bg-stone-300"></span>
            <div className="flex items-center gap-1 label-technical text-stone-400">
              <SafeIcon name="MapPin" className="w-2.5 h-2.5" /> {capituloName || 'Global'}
            </div>
          </div>
        </div>
      </div>
      {showSpecialty && (
        <div className="flex-1 space-y-4 relative z-10">
          {member.ofrezco != null && (
            <div className="bg-[#74ACDF]/5 p-4 rounded-xl border border-[#74ACDF]/10">
              <p className="label-technical text-stone-400 mb-2">Ofrezco</p>
              <p className="text-sm text-[#1B2A4E] line-clamp-2 leading-relaxed font-medium italic">"{member.ofrezco}"</p>
            </div>
          )}
          {member.busco != null && (
            <div>
              <p className="label-technical text-stone-400 mb-2">Busco</p>
              <p className="text-sm text-stone-500 line-clamp-2 leading-relaxed">{member.busco}</p>
            </div>
          )}
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-stone-50 flex items-center justify-between relative z-10">
        <span className="text-[10px] font-bold text-stone-300 tracking-widest uppercase">
          {isAuthenticated ? 'Ver Perfil' : 'Ingresar'}
        </span>
        <SafeIcon name="ArrowRight" className="w-4 h-4 text-[#74ACDF] group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
};

export const EventCard = ({ e, capituloName, isAttending, toggleRsvp, currentUser, delay = 0 }) => {
  const { reactions, toggleReaction } = useApp();
  const date = new Date(e.fecha);

  const eventReactions = reactions.filter(r => r.item_id === e.id);
  const hasReacted = eventReactions.some(r => r.member_id === currentUser?.id);

  const handleReaction = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    try {
      await toggleReaction(e.id, 'evento');
    } catch (err) {
      console.error('toggleReaction failed:', err);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="bg-white p-6 md:p-8 rounded-[32px] border border-stone-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row gap-8 items-center group relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-[#74ACDF] opacity-0 group-hover:opacity-100 transition-opacity" />
      
      <div className="bg-[#74ACDF]/10 rounded-2xl p-6 min-w-[120px] text-center border border-[#74ACDF]/20 shrink-0">
        <div className="label-technical text-[#74ACDF] mb-1">{date.toLocaleString('es', { month: 'short' })}</div>
        <div className="text-4xl font-editorial font-bold text-[#1B2A4E]">{date.getDate()}</div>
      </div>

      <div className="flex-1 text-center md:text-left">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3">
          <span className="bg-[#F6B40E]/10 text-[#F6B40E] text-xs px-3 py-1.5 rounded-full label-technical">
            {capituloName || 'Virtual'}
          </span>
          {e.modalidad && (
            <span className="bg-stone-100 text-stone-500 text-xs px-3 py-1.5 rounded-full label-technical border border-stone-200">
              {e.modalidad}
            </span>
          )}
        </div>
        
        <Link to={`/evento/${e.id}`} className="text-2xl font-editorial font-bold text-[#1B2A4E] mb-3 hover:text-[#74ACDF] transition-colors inline-block">
          {e.titulo}
        </Link>
        <p className="text-stone-500 text-sm mb-4 leading-relaxed max-w-2xl">{e.descripcion}</p>
        
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-stone-500 font-medium">
          <div className="flex items-center gap-1.5 label-technical">
            <SafeIcon name="Clock" className="w-4 h-4 text-[#F6B40E]" />
            {date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
          </div>
          {e.ubicacion && (
            <div className="flex items-center gap-1.5">
              <SafeIcon name="MapPin" className="w-4 h-4 text-stone-400" />
              {e.ubicacion}
            </div>
          )}
          
          <button onClick={handleReaction} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors shadow-sm ml-2 ${hasReacted ? 'bg-[#74ACDF]/10 text-[#74ACDF] border border-[#74ACDF]/20' : 'bg-stone-50 text-stone-500 border border-stone-200 hover:bg-stone-100'}`}>
            <SafeIcon name="ArrowUp" className="w-3.5 h-3.5" />
            <span>Upvote ({eventReactions.length})</span>
          </button>
        </div>
      </div>

      <div className="w-full md:w-auto shrink-0 mt-4 md:mt-0 flex flex-col gap-3">
        {!e.isPast ? (
          <button onClick={async () => { try { await toggleRsvp(e.id); } catch (err) { console.error('toggleRsvp failed:', err); } }} className={`w-full md:w-auto px-8 py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-3 ${isAttending ? 'bg-stone-100 text-stone-500 hover:bg-stone-200' : 'bg-[#1B2A4E] text-white hover:bg-[#253969] shadow-lg hover:shadow-xl hover:-translate-y-0.5'}`}>
            {isAttending ? <><SafeIcon name="Check" className="w-5 h-5" /> Confirmado</> : 'Confirmar Asistencia'}
          </button>
        ) : (
          e.recording_training_id && (
            <button className="w-full md:w-auto px-8 py-4 rounded-2xl font-bold bg-[#74ACDF]/10 text-[#74ACDF] hover:bg-[#74ACDF]/20 transition-colors flex items-center justify-center gap-2">
              <SafeIcon name="Play" className="w-5 h-5" /> Ver Grabación
            </button>
          )
        )}
      </div>
    </motion.div>
  );
};

export const ClubCard = ({ club, capituloName }) => {
  const { reactions, toggleReaction, currentUser } = useApp();
  
  const clubReactions = reactions.filter(r => r.item_id === club.id);
  const hasReacted = clubReactions.some(r => r.member_id === currentUser?.id);

  const handleReaction = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleReaction(club.id, 'club');
    } catch (err) {
      console.error('toggleReaction failed:', err);
    }
  };

  return (
    <Link to={`/club/${club.id}`} className="bg-white rounded-[32px] border border-stone-100 overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group block relative flex flex-col h-full">
      <div className="absolute top-0 left-0 w-full h-1 bg-[#F6B40E] opacity-0 group-hover:opacity-100 transition-opacity z-10" />
      <div className="h-48 relative overflow-hidden shrink-0">
        <img src={club.foto_portada || club.foto_url} alt={club.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4E]/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
        <div className="absolute bottom-4 left-4 flex gap-2">
          <span className="bg-[#F6B40E] text-[#1B2A4E] text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider flex items-center gap-1.5">
            <ClubTypeIcon tipo={club.tipo} /> {club.tipo}
          </span>
          <span className="bg-white/20 backdrop-blur-md text-white border border-white/30 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">
            {capituloName}
          </span>
        </div>
      </div>
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E] mb-3 leading-tight group-hover:text-[#74ACDF] transition-colors">{club.nombre}</h3>
        <p className="text-stone-500 text-sm line-clamp-2 leading-relaxed flex-1">{club.descripcion}</p>
        
        <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between">
          <button 
            onClick={handleReaction} 
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors shadow-sm ${hasReacted ? 'bg-red-50 text-red-500 border border-red-200' : 'bg-stone-50 text-stone-500 border border-stone-200 hover:bg-stone-100'}`}
          >
            <SafeIcon name="Heart" className={`w-3.5 h-3.5 ${hasReacted ? 'fill-current' : ''}`} />
            <span>Me interesa ({clubReactions.length})</span>
          </button>
          
          <div className="w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center group-hover:bg-[#74ACDF]/10 transition-colors">
            <SafeIcon name="ArrowRight" className="w-4 h-4 text-[#1B2A4E] group-hover:text-[#74ACDF] group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export const EmptyState = ({ icon, title, message }) => (
  <div className="flex flex-col items-center justify-center py-20 px-4 text-center bg-white rounded-[32px] border border-stone-100 shadow-sm">
    <div className="w-20 h-20 bg-[#FAF9F6] rounded-full flex items-center justify-center text-[#74ACDF] mb-6 shadow-sm">
      <SafeIcon name={icon} className="w-10 h-10" />
    </div>
    <h3 className="text-2xl font-editorial font-bold text-[#1B2A4E] mb-3">{title}</h3>
    <p className="text-stone-500 max-w-md leading-relaxed">{message}</p>
  </div>
);